import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import logo from '../Logo.png';

function useOutsideAlerter(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, callback]);
}

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);

  useOutsideAlerter(mobileMenuRef, () => {
    if (isMenuOpen) setIsMenuOpen(false);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNavClick = (path) => {
    if (location.pathname === path) window.scrollTo(0, 0);
    setIsMenuOpen(false);
  };

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Products', href: '/products' },
    { name: 'About', href: '/about' },
    { name: 'Updates', href: '/updates' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) =>
    location.pathname === path || (path === '/updates' && location.pathname === '/blog');

  const navLinkClass = (path) =>
    `text-base font-semibold tracking-wide transition-colors duration-200 pb-0.5 border-b-2 ${
      isActive(path)
        ? 'text-blue-700 border-blue-600'
        : 'text-gray-700 border-transparent hover:text-blue-600 hover:border-blue-300'
    }`;

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header ref={mobileMenuRef} className="fixed top-0 w-full bg-white/95 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px] md:h-20">
            <Link to="/" className="flex items-center shrink-0 min-w-0" onClick={() => handleNavClick('/')}>
              <img
                src={logo}
                alt="Arabian Link Automatic Doors"
                className="h-14 md:h-16 w-auto max-w-[200px] sm:max-w-[240px] object-contain object-left"
              />
            </Link>

            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={navLinkClass(item.href)}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => handleNavClick('/contact')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-7 py-2.5 rounded-lg text-base font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Get Quote
              </Link>
            </nav>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-4 py-4 space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={`block text-base font-semibold ${isActive(item.href) ? 'text-blue-600' : 'text-gray-700'}`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => handleNavClick('/contact')}
                className="w-full block text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg text-base font-semibold mt-4"
              >
                Get Quote
              </Link>
            </div>
          </motion.div>
        )}
      </header>

      <main className="pt-[72px] md:pt-20 overflow-x-hidden">
        {children}
      </main>

      <footer className="bg-gray-900 text-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="col-span-full lg:col-span-2">
              <div className="mb-4">
                <img
                  src={logo}
                  alt="Arabian Link Automatic Doors"
                  className="h-16 md:h-[72px] w-auto max-w-[240px] object-contain object-left"
                />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed max-w-xl">
                Leading provider of automatic door solutions across the Middle East.
                We specialize in sliding, swing, revolving, telescopic, and hermetic automatic doors
                for commercial, residential, and industrial applications.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <div className="flex items-center text-sm text-gray-300">
                  <Phone size={16} className="mr-2 text-blue-400 shrink-0" />
                  +971 50 494 6149
                </div>
                <div className="flex items-center text-sm text-gray-300 break-all">
                  <Mail size={16} className="mr-2 text-blue-400 shrink-0" />
                  arabianlinkdoors@gmail.com
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 col-span-full lg:contents">
              <div>
                <h3 className="text-lg font-semibold mb-4">Products</h3>
                <ul className="space-y-2">
                  <li><Link to="/products" className="text-gray-300 hover:text-blue-400 transition-colors">Sliding Doors</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-blue-400 transition-colors">Swing Doors</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-blue-400 transition-colors">Revolving Doors</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-blue-400 transition-colors">Telescopic Doors</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-blue-400 transition-colors">Hermetic Doors</Link></li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">Services</h3>
                <ul className="space-y-2">
                  <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors">Installation</Link></li>
                  <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors">Maintenance</Link></li>
                  <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors">Repairs</Link></li>
                  <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors">Custom Solutions</Link></li>
                  <li><Link to="/contact" className="text-gray-300 hover:text-blue-400 transition-colors">Consultation</Link></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © 2026 Arabian Link Doors. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-blue-400 text-sm transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-blue-400 text-sm transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
